import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Image,
  StyleSheet,
  Text,
  TextInput,
  View,
} from './components/base_components';
import { useBusy, BusyOverlay } from './components/busy_component';
import InlineTextButton from './components/buttons/inline_text_button';
import TextButton from './components/buttons/text_button';

import UserStore from './stores/user_store';

const styles = StyleSheet.create({
  loginScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#ededed',
  },
  topLine: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: '25rem',
    height: '5rem',
    filter: 'var(--icon-login-dialog)',
  },
  boxView: {
    alignSelf: 'stretch',
    margin: '2rem',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    padding: '3rem',
    maxWidth: '40rem',
    backgroundColor: '#fefefe',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    borderWidth: 1,
    borderColor: '#c7c7c7',
  },
  title: {
    color: '#333',
    fontSize: '2rem',
    fontWeight: '600',
  },
  email: {
    alignSelf: 'stretch',
    marginTop: '1rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '4rem',
    color: '#555',
    fontSize: '1.5rem',
    borderRadius: '0.5rem',
    borderWidth: 1,
    borderColor: '#555',
    backgroundColor: '#f1f1f1',
  },
  subtitle: {
    color: '#333',
    fontSize: '1.5rem',
    fontWeight: '400',
  },
  line: {
    marginTop: '1rem',
    flexDirection: 'row',
  },
  middleSpace: {
    height: '3rem',
  },
  button: {
    alignSelf: 'flex-end',
    marginTop: '1rem',
  },
  text: {
    color: '#333',
    fontSize: '1.5rem',
    fontWeight: '400',
  },
  link: {
    marginLeft: '1rem',
    color: '#333',
    fontSize: '1.5rem',
    fontWeight: '400',
    textDecoration: 'underline',
  },
});

const LOGO = require('./assets/img/logo.png');

export default function LoginScreen() {
  const [email, setEmail] = React.useState('');
  const [login_code, setLoginCode] = React.useState('');
  const [is_busy, setBusy, clearBusy] = useBusy();
  const [show_code, setShowCode] = React.useState(false);
  const [show_not_member, setShowNotMember] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dest =
    (location?.state?.from === '/logout' ? '/' : location?.state?.from) ?? '/';

  function _onSendCode() {
    if (email && setBusy()) {
      UserStore.emailCode({ email }, (err) => {
        clearBusy();
        if (err === 404) {
          window.alert('User not found.  Please signup before logging in.');
        } else if (err) {
          window.alert('Failed to send code.');
        } else {
          setShowCode(true);
        }
      });
    }
  }
  function _onSignin() {
    if (email && login_code && setBusy()) {
      UserStore.loginCode({ email, login_code }, (err) => {
        if (err === 412) {
          setShowNotMember(true);
        } else if (err) {
          window.alert('Login failed, please check your code.');
        } else {
          navigate(dest);
        }
        clearBusy();
      });
    }
  }
  function _onLoginCancel() {
    setEmail('');
    setLoginCode('');
    setShowCode(false);
    setShowNotMember(false);
  }
  let content: any = null;
  if (show_not_member) {
    content = (
      <View style={styles.box}>
        <Text style={styles.title}>Not A Member</Text>
        <View style={styles.middleSpace} />
        <Text style={styles.subtitle}>
          You're not a member of this Organization. Please contact your
          administrator and have him invite you to this organization.
        </Text>
        <View style={styles.middleSpace} />
        <View style={styles.line}>
          <Text style={styles.text}>Try Again?</Text>
          <InlineTextButton
            style={styles.link}
            underlayColor='rgba(0,0,0,0.1)'
            text='Go back'
            onPress={_onLoginCancel}
          />
        </View>
      </View>
    );
  } else if (show_code) {
    content = (
      <View style={styles.box}>
        <Text style={styles.title}>Enter code</Text>
        <View style={styles.middleSpace} />
        <Text style={styles.subtitle}>
          We emailed a code to {email}. Please enter the code to sign in.
        </Text>
        <View style={styles.line}>
          <Text style={styles.text}>Didn't get a code?</Text>
          <InlineTextButton
            style={styles.link}
            underlayColor='rgba(0,0,0,0.1)'
            text='Resend'
            onPress={_onSendCode}
          />
        </View>
        <TextInput
          style={styles.email}
          placeholder='code'
          value={login_code}
          onChangeText={setLoginCode}
          autoFocus={true}
          onSubmitEditing={_onSignin}
        />
        <TextButton style={styles.button} text='Sign in' onPress={_onSignin} />
        <View style={styles.middleSpace} />
        <View style={styles.line}>
          <Text style={styles.text}>Wrong email?</Text>
          <InlineTextButton
            style={styles.link}
            underlayColor='rgba(0,0,0,0.1)'
            text='Go back'
            onPress={_onLoginCancel}
          />
        </View>
      </View>
    );
  } else {
    content = (
      <View style={styles.box}>
        <Text style={styles.title}>Sign in</Text>
        <TextInput
          style={styles.email}
          placeholder='email'
          value={email}
          onChangeText={setEmail}
          autoFocus={true}
          onSubmitEditing={_onSendCode}
        />
        <TextButton
          style={styles.button}
          text='Continue'
          onPress={_onSendCode}
        />
      </View>
    );
  }

  return (
    <View style={styles.loginScreen}>
      <View style={styles.topLine}>
        <Image style={styles.image} source={LOGO} />
      </View>
      <View style={styles.boxView}>{content}</View>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
